import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from 'rxjs/operators';

import { APP_CONFIG } from '@app/shared/config/app.config';
import { IDtoResponse } from '@app/shared/models/dtos/response.dto';
import { AppContext } from '@app/shared/services/context/app.context.service';
import {
  IInstallmentLoansHistoryResponseDTO,
  IInstallmentLoanDetailResponseDTO,
  IInstallmentLoanMovementsResponseDTO,
  IInstallmentLoanAmortizationsResponseDTO,
  IInstallmentLoanStatementsResponseDTO,
  IInstallmentLoanNegotiationsResponseDTO,
} from '@app/shared/models/installment-loans.model';
import { ServiceBase } from '@app/shared/services/base/base.service';
import { filtersToParams } from '@app/shared/utils/filters-params';
import { CollectionParam } from '@app/shared/models/param.model';
import { PATH } from '@app/shared/types/enums/path';
import { ActionEnum } from '@app/shared/types/enums/ActionEnum.enum';
import { channel } from 'version';

@Injectable({
  providedIn: 'root',
})
export class InstallmentLoanService extends ServiceBase {
  constructor(private readonly _http: HttpClient, private readonly _appCtx: AppContext) {
    super(`${APP_CONFIG.serviceBaseUrl}/lcm/api/v1/installment-loans`);
  }

  getInstallmentLoansHistory(rppUserId: string): Observable<IDtoResponse<IInstallmentLoansHistoryResponseDTO>> {
    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.INSTALLMENTS_LOANS)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.READ)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this._http.get<IDtoResponse<IInstallmentLoansHistoryResponseDTO>>(`${this.baseUrl}/${rppUserId}/history`, { headers });
  }

  getInstallmentLoanDetails(rppUserId: string, loanCode: string): Observable<IDtoResponse<IInstallmentLoanDetailResponseDTO>> {
    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.INSTALLMENTS_LOANS)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.READ)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this._http.get<IDtoResponse<IInstallmentLoanDetailResponseDTO>>(`${this.baseUrl}/${rppUserId}/details/${loanCode}`, { headers });
  }

  getInstallmentLoanMovements(
    rppUserId: string,
    loanCode: string,
    collectionParam: CollectionParam
  ): Observable<IDtoResponse<IInstallmentLoanMovementsResponseDTO>> {
    const filters = filtersToParams({ ...collectionParam.filters });

    const params = new HttpParams({
      fromObject: {
        pageSize: collectionParam.pageSize,
        pageIndex: collectionParam.pageIndex,
        ...filters,
      },
    });

    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.INSTALLMENTS_LOANS)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.READ)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this._http.get<IDtoResponse<IInstallmentLoanMovementsResponseDTO>>(`${this.baseUrl}/${rppUserId}/movements/${loanCode}`, {
      params,
      headers,
    });
  }

  getInstallmentLoanAmortizations(
    rppUserId: string,
    loanCode: string,
    collectionParam: CollectionParam
  ): Observable<IDtoResponse<IInstallmentLoanAmortizationsResponseDTO>> {
    const filters = filtersToParams({ ...collectionParam.filters });

    const params = new HttpParams({
      fromObject: {
        pageSize: collectionParam.pageSize,
        pageIndex: collectionParam.pageIndex,
        ...filters,
      },
    });

    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.INSTALLMENTS_LOANS)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.READ)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this._http.get<IDtoResponse<IInstallmentLoanAmortizationsResponseDTO>>(
      `${this.baseUrl}/${rppUserId}/amortizations/${loanCode}`,
      {
        params,
        headers,
      }
    );
  }

  getInstallmentLoanStatementsInfo(rppUserId: string, loanCode: string): Observable<IInstallmentLoanStatementsResponseDTO | undefined> {
    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.INSTALLMENTS_LOANS)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.READ)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    const params = new HttpParams({
      fromObject: {
        pageSize: '100',
        pageIndex: '1',
      },
    });

    return this._http
      .get<IDtoResponse<IInstallmentLoanStatementsResponseDTO>>(`${this.baseUrl}/${rppUserId}/statements/${loanCode}`, {
        headers,
        params,
      })
      .pipe(map(res => res.data));
  }

  getInstallmentLoanStatementPdf(params: { rppUserId: string; loanCode: string; statementCode: string }): Observable<any> {
    const { rppUserId, loanCode, statementCode } = params;
    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.INSTALLMENTS_LOANS)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.READ)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this._http
      .get<IDtoResponse<any>>(`${this.baseUrl}/${rppUserId}/statements/${loanCode}/download/${statementCode}`, {
        headers,
      })
      .pipe(map(res => res.data));
  }

  getInstallmentLoanNegotiations(
    rppUserId: string,
    loanCode: string,
    collectionParam: CollectionParam
  ): Observable<IDtoResponse<IInstallmentLoanNegotiationsResponseDTO>> {
    const filters = filtersToParams({ ...collectionParam.filters });

    const params = new HttpParams({
      fromObject: {
        pageSize: collectionParam.pageSize,
        pageIndex: collectionParam.pageIndex,
        ...filters,
      },
    });

    const headers = new HttpHeaders()
      .set('monitoring_view', PATH.INSTALLMENTS_LOANS)
      .set('monitoring_traceability_id', this._appCtx.getTraceSessionId())
      .set('monitoring_action', ActionEnum.READ)
      .set('channel', channel + '-' + this._appCtx.getUserType());

    return this._http.get<IDtoResponse<IInstallmentLoanNegotiationsResponseDTO>>(
      `${this.baseUrl}/${rppUserId}/negotiations/${loanCode}/history`,
      {
        params,
        headers,
      }
    );
  }
}